import React, { useState } from 'react';
import Modal from '../../common/Modal';
import SaveCancelButtons from '../../common/buttons/SaveCancelButtons';
import Label from '../../common/Label';
import { styled } from 'styled-components';
import { updateProject } from '../../../services/project-service';
import BusySpinner from '../../common/BusySpinner';
import { Project } from '../../../data/models/project';
import Select from '../../common/form/Select';
import ModalHeader from '../../common/ModalHeader';
import Dialog, {
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
} from '../../common/Dialog';
import Button from '../../common/buttons/Button';
import { useDispatch } from 'react-redux';
import {
	asyncCallDone,
	asyncCallStart,
	refreshProjectSuccess,
} from '../../../data/stores/projectStore';
import { showAlert } from '../../../data/stores/alertStore';

interface ProjectSubStatusChangeModalProps {
	open: boolean;
	handleClose: () => void;
	project: Project;
}

const subStatusOptions = [
	{ id: 'CURRENT', display: 'Current' },
	{ id: 'DEVELOPMENT', display: 'Development' },
	{ id: 'BUILDING', display: 'Building' },
];

export default function ProjectSubStatusChangeModal(
	props: ProjectSubStatusChangeModalProps
) {
	const { open, handleClose, project } = props;

	const dispatch = useDispatch();
	const [newSubStatus, setNewSubStatus] = useState<string>('');
	const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

	const handleUpdateProjectSubStatus = async () => {
		try {
			handleClose();
			setNewSubStatus('');
			dispatch(asyncCallStart());
			const updatedProject = await updateProject(project.id, {
				subStatus: newSubStatus,
			});
			dispatch(refreshProjectSuccess(updatedProject));
			dispatch(
				showAlert({
					message: 'Project sub-status updated successfully!',
					severity: 'success',
				})
			);
		} catch (error) {
			dispatch(
				showAlert({
					message: 'An error occurred updating the project sub-status.',
					severity: 'error',
				})
			);
		} finally {
			dispatch(asyncCallDone());
		}
	};

	const getCurrentSubStatusDisplay = () => {
		const option = subStatusOptions.find(opt => opt.id === project.subStatus);
		return option ? option.display : project.subStatus || 'Not Set';
	};

	const getNewSubStatusDisplay = () => {
		const option = subStatusOptions.find(opt => opt.id === newSubStatus);
		return option ? option.display : newSubStatus;
	};

	return (
		<Modal open={open} handleClose={handleClose}>
			<ModalHeader>Update Project Sub-Status</ModalHeader>
			<Label>Project</Label>
			<Info>{project.name}</Info>
			<Label>Current Sub-Status</Label>
			<Info>{getCurrentSubStatusDisplay()}</Info>
			<InputContainer>
				<Select
					value={newSubStatus}
					onChange={(e) => setNewSubStatus(e.target.value)}
					label={'New Sub-Status'}
					options={subStatusOptions}
					placeholder="Select new sub-status"
				/>
			</InputContainer>
			<SaveCancelButtons
				handleSave={() => setConfirmationModalOpen(true)}
				handleCancel={handleClose}
				saveText={'Review'}
				saveDisabled={!newSubStatus}
			/>
			<Dialog
				open={confirmationModalOpen}
				handleClose={() => setConfirmationModalOpen(false)}
			>
				<DialogTitle>Confirm Project Sub-Status Change</DialogTitle>
				<DialogContent>
					<Label>Current Sub-Status</Label>
					<Info>{getCurrentSubStatusDisplay()}</Info>
					<Label>New Sub-Status</Label>
					<Info>{getNewSubStatusDisplay()}</Info>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setConfirmationModalOpen(false)}
						variant={'text'}
					>
						Cancel
					</Button>
					<Button onClick={handleUpdateProjectSubStatus}>Save</Button>
				</DialogActions>
			</Dialog>
		</Modal>
	);
}

const Info = styled.div`
	margin-bottom: ${(props) => props.theme.spacing(1)};
`;

const InputContainer = styled.div`
	margin-bottom: ${(props) => props.theme.spacing(4)};
	margin-top: ${(props) => props.theme.spacing(2)};
`;
